import { useQuery } from "@tanstack/react-query";
import { fetchAllPaginated } from "@/lib/supabase-paginate";

export interface ProjectStorage {
  gb: number | null;
  location: string | null;
}

interface StorageRow {
  project_id: string | null;
  storage_gb: number | null;
  storage_location: string | null;
  created_at: string;
}

/**
 * VIB-2103: lagret datamengde per prosjekt, til «Lagring»-kolonnen på
 * avsluttede prosjekter.
 *
 * Tallet bor IKKE på `projects`. Lukkedialogen (VIB-1308) oppretter en
 * `recurring_agreements`-rad av typen `storage`, og det er den som bærer
 * `storage_gb` + `storage_location`. Kolonnen leser derfor derfra.
 *
 * KONSEKVENS VERDT Å VITE: avtalen krever en kunde. Lukkes et prosjekt uten
 * kunde, advarer dialogen, men tallet blir ikke lagret noe sted — det
 * prosjektet viser «—» uansett hva prosjektlederen skrev inn.
 *
 * FLERE AVTALER PER PROSJEKT: gjenåpning og ny lukking gir en ny rad, siden
 * flyten alltid gjør INSERT. Vi tar den NYESTE, ikke summen: prosjektlederen
 * fyller inn prosjektets totale datamengde hver gang, så en summering ville
 * telt det samme materialet to ganger.
 */
export function useProjectStorage(enabled: boolean) {
  return useQuery({
    queryKey: ["project-storage"],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Map<string, ProjectStorage>> => {
      const rows = await fetchAllPaginated<StorageRow>(
        "recurring_agreements",
        "project_id, storage_gb, storage_location, created_at",
        (q) => q.eq("type", "storage").is("deleted_at", null).not("project_id", "is", null),
        // Rekkefølgen MÅ settes her, ikke med en `.order()` i filteret over:
        // helperen legger på sin egen sortering først og pager på den, så en
        // ekstra order i filteret ville blitt sekundær og «nyeste vinner»
        // under ville sett på feil rad. `id` som tie-break holder pagineringen
        // stabil når to avtaler har samme tidsstempel.
        ["created_at", "id"],
      );

      // Stigende rekkefølge, så en senere rad overskriver en tidligere —
      // kartet ender på den nyeste per prosjekt.
      const map = new Map<string, ProjectStorage>();
      for (const r of rows) {
        if (!r.project_id) continue;
        map.set(r.project_id, {
          gb: r.storage_gb,
          location: r.storage_location,
        });
      }
      return map;
    },
  });
}
